/**
 * Hook for real-time Speech-to-Text via WebSocket.
 * Captures PCM audio from MediaStream, sends to backend, displays transcript.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';

const SAMPLE_RATE = 16000; // ElevenLabs expects 16kHz
const BUFFER_SIZE = 4096;
const TARGET_SAMPLE_RATE = 16000;
const SEND_INTERVAL_MS = 150;
const DUPLICATE_COMMIT_WINDOW_MS = 1500;

/** Maps raw ElevenLabs/backend errors to user-friendly messages */
function mapToUserFriendlyError(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes('unaccepted') || lower.includes('terms')) {
    return 'Caption requires accepting ElevenLabs terms in dashboard. Contact admin.';
  }
  if (lower.includes('auth') || lower.includes('invalid') || lower.includes('api_key') || lower.includes('401')) {
    return 'Caption service authentication failed. Contact admin.';
  }
  if (lower.includes('quota') || lower.includes('limit') || lower.includes('exceeded')) {
    return 'Caption quota exceeded. Try again later.';
  }
  if (lower.includes('rate_limit') || lower.includes('429')) {
    return 'Caption service busy. Please try again in a moment.';
  }
  if (lower.includes('websocket') || lower.includes('connection') || lower.includes('failed to connect')) {
    return 'Could not connect to caption service. Run: py manage.py runserver_ws';
  }
  return raw.length > 80 ? raw.slice(0, 80) + '…' : raw;
}

interface TranscriptSegment {
  text: string;
  isPartial: boolean;
  words?: Array<{ text: string; start: number; end: number }>;
}

export function useRealtimeSTT() {
  const [transcript, setTranscript] = useState<TranscriptSegment[]>([]);
  const [partialText, setPartialText] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const pendingPcmChunksRef = useRef<ArrayBuffer[]>([]);
  const flushIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastCommittedTextRef = useRef('');
  const lastCommittedAtRef = useRef(0);

  const mergeArrayBuffers = (buffers: ArrayBuffer[]): ArrayBuffer => {
    const totalLength = buffers.reduce((sum, buf) => sum + buf.byteLength, 0);
    const merged = new Uint8Array(totalLength);
    let offset = 0;
    for (const buf of buffers) {
      merged.set(new Uint8Array(buf), offset);
      offset += buf.byteLength;
    }
    return merged.buffer;
  };

  const floatTo16BitPCM = (float32Array: Float32Array): ArrayBuffer => {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
    return buffer;
  };

  const downsample = (input: Float32Array, fromRate: number, toRate: number): Float32Array => {
    const ratio = fromRate / toRate;
    const outLength = Math.floor(input.length / ratio);
    const output = new Float32Array(outLength);
    for (let i = 0; i < outLength; i++) {
      const srcIndex = i * ratio;
      const srcIndexFloor = Math.floor(srcIndex);
      const srcIndexCeil = Math.min(srcIndexFloor + 1, input.length - 1);
      const t = srcIndex - srcIndexFloor;
      output[i] = input[srcIndexFloor] * (1 - t) + input[srcIndexCeil] * t;
    }
    return output;
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  const start = useCallback(async (stream: MediaStream) => {
    setError(null);
    setTranscript([]);
    setPartialText('');

    const wsUrl = API_ENDPOINTS.REALTIME_STT_WS;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    pendingPcmChunksRef.current = [];
    lastCommittedTextRef.current = '';
    lastCommittedAtRef.current = 0;

    const wsOpenPromise = new Promise<void>((resolve, reject) => {
      ws.onopen = () => {
        setIsConnected(true);
        resolve();
      };
      ws.onerror = () => reject(new Error('WebSocket connection failed. Run: py manage.py runserver_ws'));
    });

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        const mt = msg.message_type;
        const appendCommittedText = (
          rawText: string | undefined,
          words?: Array<{ text: string; start: number; end: number }>
        ) => {
          const text = rawText?.trim();
          if (!text) return;
          const now = Date.now();
          const isDuplicate =
            text === lastCommittedTextRef.current &&
            now - lastCommittedAtRef.current < DUPLICATE_COMMIT_WINDOW_MS;
          if (isDuplicate) {
            return;
          }

          setTranscript((prev) => [...prev, { text, isPartial: false, words }]);
          lastCommittedTextRef.current = text;
          lastCommittedAtRef.current = now;
        };

        if (mt === 'partial_transcript') {
          setPartialText(msg.text || '');
        } else if (mt === 'committed_transcript') {
          // Some sessions emit both committed_transcript and committed_transcript_with_timestamps.
          // Keep this text, but de-duplicate repeated commits in a short window.
          appendCommittedText(msg.text);
          setPartialText('');
        } else if (mt === 'committed_transcript_with_timestamps') {
          const words = Array.isArray(msg.words)
            ? msg.words
                .filter((w: { type?: string }) => w.type === 'word')
                .map((w: { text: string; start: number; end: number }) => ({
                  text: w.text,
                  start: w.start,
                  end: w.end
                }))
            : undefined;
          appendCommittedText(msg.text, words);
          setPartialText('');
        } else if (mt === 'session_started') {
          // Backend may buffer audio until ElevenLabs session is ready.
        } else if (mt === 'error' || mt === 'auth_error' || mt === 'quota_exceeded' || mt === 'input_error' || mt === 'unaccepted_terms' || mt === 'rate_limited' || mt === 'transcriber_error') {
          const rawError = msg.error || msg.message || 'Transcription error';
          const friendlyError = mapToUserFriendlyError(rawError);
          setError(friendlyError);
        }
      } catch {
        // Ignore parse errors
      }
    };

    const flushPendingAudio = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      if (pendingPcmChunksRef.current.length === 0) return;
      const mergedPcm = mergeArrayBuffers(pendingPcmChunksRef.current);
      pendingPcmChunksRef.current = [];
      const b64 = arrayBufferToBase64(mergedPcm);
      wsRef.current.send(
        JSON.stringify({
          message_type: 'input_audio_chunk',
          audio_base_64: b64,
          commit: false,
          sample_rate: SAMPLE_RATE
        })
      );
    };

    ws.onerror = () => {
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current);
        flushIntervalRef.current = null;
      }
      setError(mapToUserFriendlyError('WebSocket connection failed. Run: py manage.py runserver_ws'));
    };

    ws.onclose = () => {
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current);
        flushIntervalRef.current = null;
      }
      setIsConnected(false);
    };

    try {
      await wsOpenPromise;
    } catch (e) {
      setError(mapToUserFriendlyError(e instanceof Error ? e.message : 'Connection failed'));
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    streamRef.current = stream;
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 48000 });
    audioContextRef.current = audioContext;

    const source = audioContext.createMediaStreamSource(stream);
    sourceRef.current = source;

    const processor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);
    processorRef.current = processor;

    flushIntervalRef.current = setInterval(() => {
      flushPendingAudio();
    }, SEND_INTERVAL_MS);

    processor.onaudioprocess = (e) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);
      const downsampled = downsample(input, 48000, TARGET_SAMPLE_RATE);
      const pcm = floatTo16BitPCM(downsampled);
      pendingPcmChunksRef.current.push(pcm);
    };

    source.connect(processor);
    const silentGain = audioContext.createGain();
    silentGain.gain.value = 0;
    processor.connect(silentGain);
    silentGain.connect(audioContext.destination);
  }, []);

  const stop = useCallback(() => {
    if (flushIntervalRef.current) {
      clearInterval(flushIntervalRef.current);
      flushIntervalRef.current = null;
    }

    // Flush queued audio before commit/close to reduce transcript loss.
    if (wsRef.current?.readyState === WebSocket.OPEN && pendingPcmChunksRef.current.length > 0) {
      const mergedPcm = mergeArrayBuffers(pendingPcmChunksRef.current);
      pendingPcmChunksRef.current = [];
      const b64 = arrayBufferToBase64(mergedPcm);
      try {
        wsRef.current.send(
          JSON.stringify({
            message_type: 'input_audio_chunk',
            audio_base_64: b64,
            commit: false,
            sample_rate: SAMPLE_RATE
          })
        );
      } catch {
        // Ignore
      }
    }

    // Stop sending audio first
    if (processorRef.current && sourceRef.current && audioContextRef.current) {
      try {
        sourceRef.current.disconnect();
        processorRef.current.disconnect();
        processorRef.current = null;
        sourceRef.current = null;
      } catch {
        // Ignore
      }
      audioContextRef.current?.close();
      audioContextRef.current = null;
    }
    // Commit any final partial transcript before closing
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(
          JSON.stringify({
            message_type: 'input_audio_chunk',
            audio_base_64: '',
            commit: true,
            sample_rate: SAMPLE_RATE
          })
        );
      } catch {
        // Ignore
      }
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    pendingPcmChunksRef.current = [];
    setIsConnected(false);
  }, []);

  const commit = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          message_type: 'input_audio_chunk',
          audio_base_64: '',
          commit: true,
          sample_rate: SAMPLE_RATE
        })
      );
    }
  }, []);

  const reset = useCallback(() => {
    setTranscript([]);
    setPartialText('');
    setError(null);
  }, []);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return {
    transcript,
    partialText,
    isConnected,
    error,
    start,
    stop,
    commit,
    reset,
    fullText: [...transcript.map((s) => s.text), partialText].filter(Boolean).join(' ')
  };
}
